/**
 * Rotation for the featured hero (spec §4.2). Only with more than one slide. A visible pause control,
 * previous/next and one button per slide; the arrow keys move it when the region has focus; hover and
 * focus inside the slides hold it; a hidden tab does not advance; it never auto-advances under
 * prefers-reduced-motion. In the static render the first slide is active and nothing runs.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

export const INTERVAL_MS = 7000;

export function useRotation(n) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);
  const held = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(!!mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => { setI((x) => (n ? Math.min(x, n - 1) : 0)); }, [n]);

  const go = useCallback((d) => setI((x) => (n ? (x + d + n) % n : 0)), [n]);

  useEffect(() => {
    if (paused || reduced || n < 2) return undefined;
    const t = setInterval(() => { if (!held.current && !document.hidden) go(1); }, INTERVAL_MS);
    return () => clearInterval(t);
  }, [paused, reduced, n, go]);

  const holdProps = n > 1 ? {
    onMouseEnter: () => { held.current = true; },
    onMouseLeave: () => { held.current = false; },
    onFocus: () => { held.current = true; },
    onBlur: () => { held.current = false; },
    onKeyDown: (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
    },
  } : {};

  return { i, setI, go, paused, setPaused, reduced, rotating: !paused && !reduced && n > 1, holdProps };
}

export function RotationControls({ titles, rotation }) {
  const n = titles.length;
  const { i, setI, go, paused, setPaused, reduced, rotating } = rotation;
  return (
    <div className="ev-rotation" role="group" aria-label="Featured events rotation">
      <div className="ev-dots">
        {(titles || []).map((t, j) => (
          <button key={j} type="button" className="ev-dot" data-testid={`hero-rotation-dot-${j}`} aria-label={`Featured event ${j + 1} of ${n}: ${t}`} aria-current={j === i ? "true" : undefined} onClick={() => setI(j)} />
        ))}
      </div>
      <div className="ev-rotation-ctrls">
        <span className="ev-pos" aria-live="polite">{i + 1} / {n}</span>
        <button type="button" className="ev-ctrl" data-testid="hero-rotation-prev" aria-label="Previous featured event" onClick={() => go(-1)}><ChevronLeft size={18} aria-hidden="true" /></button>
        <button type="button" className="ev-ctrl" data-testid="hero-rotation-next" aria-label="Next featured event" onClick={() => go(1)}><ChevronRight size={18} aria-hidden="true" /></button>
        {/* The visible label says what pressing it does (APG carousel pattern). Under reduced motion the state is in the visible label, not a hover-only title. */}
        <button type="button" className="ev-ctrl ev-ctrl-pause" data-testid="hero-rotation-pause" onClick={() => setPaused((p) => !p)} disabled={reduced} aria-label={reduced ? "Rotation is off because your device prefers reduced motion" : undefined}>
          {rotating ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
          <span>{reduced ? "Rotation off" : rotating ? "Pause" : paused ? "Play" : "Play"}</span>
        </button>
      </div>
    </div>
  );
}
